/**
 * Product Family Model
 *
 * Defines the structure for product families (Surface Products, Furniture, Lighting, etc.)
 * Each family has its own attributes, variant axes, and visualization configuration.
 */

import { TranslatableString } from '../utils/translatable-string';

// =============================================================================
// Core Types
// =============================================================================

export type AttributeFieldType =
  | 'text'
  | 'number'
  | 'select'
  | 'multioption'
  | 'textarea'
  | 'checkbox'
  | 'color'
  | 'dimensions'
  | 'images'
  | 'url';

export type AttributeCategory = 'core' | 'family_specific';

export type VariantAxisValueType = 'string' | 'dimensions' | 'number';

// =============================================================================
// Attribute Interfaces
// =============================================================================

export interface AttributeValidation {
  min?: number;
  max?: number;
  pattern?: string;
  message?: string;
}

export interface FamilyAttribute {
  /** Internal field name (snake_case): "grout_color", "material" */
  fieldName: string;
  /** Display label (translatable): "Grout Color", "Material" */
  fieldLabel: TranslatableString;
  /** Field input type */
  fieldType: AttributeFieldType;
  /** Whether this is a core or family-specific attribute */
  category: AttributeCategory;
  /** Whether this field is required */
  required: boolean;
  /** Default value for the field */
  defaultValue?: any;
  /** Placeholder text for input (translatable) */
  placeholder?: TranslatableString;
  /** Help text shown below the field (translatable) */
  helpText?: TranslatableString;
  /** Options for select/multioption fields (translatable) */
  options?: (string | Record<string, string>)[];
  /** Validation rules */
  validation?: AttributeValidation;
  /** Show this field in product list table */
  visibleInList: boolean;
  /** Show this field in visualizer filter panel */
  visibleInFilters: boolean;
  /** Display order in forms */
  order: number;
  /** Unit label for number fields (e.g., "cm", "kg") */
  unit?: string;
}

// =============================================================================
// Variant Axis Configuration
// =============================================================================

export interface VariantAxisConfig {
  /** Axis name: "color", "size", "pattern", "material" */
  name: string;
  /** Display label (translatable) */
  label: TranslatableString;
  /** Whether this axis is enabled for the family */
  enabled: boolean;
  /** Whether variants must have a value for this axis */
  required: boolean;
  /** Value type for this axis */
  valueType: VariantAxisValueType;
  /** Whether vendors can add custom values */
  allowCustomValues: boolean;
  /** Pre-defined values (for select-like axes) */
  predefinedValues?: string[];
  /** Number of dimensions for 'dimensions' valueType: 2 (width x length) or 3 (width x length x height) */
  dimensionCount?: 2 | 3;
  /**
   * Whether variants with the same value for this axis share plank images.
   * When true, this axis is used to compute plankGroupId.
   * Example: if color and size have isGroupingAxis: true, then
   * C1/20x20/bricks and C1/20x20/herringbone share the same plank images.
   */
  isGroupingAxis?: boolean;
}

// =============================================================================
// Visualization Configuration
// =============================================================================

export interface VisualizationConfig {
  /** Whether products in this family need seamless texture generation */
  requiresSeamlessTexture: boolean;
  /** Whether products in this family need 3D model support (future) */
  requires3DModel: boolean;
  /** Whether to show plank image upload mode */
  supportsPlankImages: boolean;
  /** Whether pattern-based seamless generation is available */
  supportsPatterns: boolean;
  /** Available pattern options (herringbone, brick, etc.) */
  patternOptions?: string[];
  /** Default application types (floor, walls, etc.) */
  defaultApplicationTypes?: string[];
  /** Whether to show Application Type selector (floor/walls) - Surface Products specific */
  showApplicationTypes?: boolean;
  /** Whether to show Product Type selector (hardwood/tile) - Surface Products specific */
  showProductType?: boolean;
}

// =============================================================================
// Import Configuration
// =============================================================================

export interface CsvColumnConfig {
  /** Column name in CSV */
  columnName: string;
  /** Maps to which product field */
  mapsToField: string;
  /** Whether this column is required */
  required: boolean;
  /** Example value for documentation */
  example: string;
}

export interface ImageRequirement {
  /** Image type */
  type: 'thumbnail' | 'seamless' | 'plank' | 'product_image' | '3d_model';
  /** Whether this image type is required */
  required: boolean;
  /** Accepted file formats */
  acceptedFormats: string[];
  /** Max file size in bytes */
  maxSize?: number;
  /** Min dimensions */
  minDimensions?: { width: number; height: number };
}

export interface ImportConfig {
  /** CSV template column configuration */
  csvTemplateColumns: CsvColumnConfig[];
  /** Image requirements for this family */
  requiredImages: ImageRequirement[];
}

// =============================================================================
// Product Family Interface
// =============================================================================

export interface ProductFamily {
  /** MongoDB ObjectId */
  _id: string;
  /** Family display name (translatable): "Surface Products", "Furniture" */
  name: TranslatableString;
  /** URL-safe slug: "surface-products", "furniture" */
  slug: string;
  /** Family description (translatable) */
  description?: TranslatableString;
  /** Icon URL or icon key */
  icon?: string;
  /** Whether this family is active and usable */
  isActive: boolean;
  /** Whether this is the default family for new products */
  isDefault?: boolean;
  /** Whether this is a system family (cannot be deleted) */
  isSystemFamily?: boolean;
  /** Family attribute definitions */
  attributes: FamilyAttribute[];
  /** Variant axis configuration */
  variantAxes: VariantAxisConfig[];
  /** Visualization requirements */
  visualizationConfig: VisualizationConfig;
  /** Import configuration */
  importConfig?: ImportConfig;
  /** Display order in UI */
  order: number;
  /** Creation timestamp */
  createdAt: Date;
  /** Last update timestamp */
  updatedAt: Date;
}

// =============================================================================
// API Payloads
// =============================================================================

export interface CreateProductFamilyPayload {
  name: TranslatableString;
  slug: string;
  description?: TranslatableString;
  icon?: string;
  isActive?: boolean;
  isDefault?: boolean;
  attributes?: FamilyAttribute[];
  variantAxes?: VariantAxisConfig[];
  visualizationConfig?: Partial<VisualizationConfig>;
  importConfig?: Partial<ImportConfig>;
}

export interface UpdateProductFamilyPayload {
  name?: TranslatableString;
  description?: TranslatableString;
  icon?: string;
  isActive?: boolean;
  isDefault?: boolean;
  attributes?: FamilyAttribute[];
  variantAxes?: VariantAxisConfig[];
  visualizationConfig?: Partial<VisualizationConfig>;
  importConfig?: Partial<ImportConfig>;
  order?: number;
}

// =============================================================================
// Utility Types
// =============================================================================

/** Vendor's enabled families (stored on vendor document) */
export interface VendorFamilyAssignment {
  vendorId: string;
  enabledFamilyIds: string[];
}

/** Family attributes stored on a product */
export type ProductFamilyAttributes = Record<string, any>;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a family supports seamless texture generation
 */
export function familySupportsSeamless(family: ProductFamily): boolean {
  return family.visualizationConfig?.requiresSeamlessTexture ?? false;
}

/**
 * Check if a family supports 3D models
 */
export function familySupports3D(family: ProductFamily): boolean {
  return family.visualizationConfig?.requires3DModel ?? false;
}

/**
 * Get required attributes for a family
 */
export function getRequiredAttributes(family: ProductFamily): FamilyAttribute[] {
  return family.attributes.filter(attr => attr.required);
}

/**
 * Get family-specific attributes (non-core)
 */
export function getFamilySpecificAttributes(family: ProductFamily): FamilyAttribute[] {
  return family.attributes.filter(attr => attr.category === 'family_specific');
}

/**
 * Get core attributes
 */
export function getCoreAttributes(family: ProductFamily): FamilyAttribute[] {
  return family.attributes.filter(attr => attr.category === 'core');
}

/**
 * Get enabled variant axes
 */
export function getEnabledVariantAxes(family: ProductFamily): VariantAxisConfig[] {
  return family.variantAxes.filter(axis => axis.enabled);
}

/**
 * Check if a field type is a selection type (has options)
 */
export function isSelectionFieldType(fieldType: AttributeFieldType): boolean {
  return fieldType === 'select' || fieldType === 'multioption';
}
